import { AutoConvertToggle } from "./common.jsx";
import React, { useEffect, useRef, useState } from "react";
import {
  useApp,
  useTranslate,
  FontText,
  TypingInput,
  Close,
  Icon,
  loadFont,
} from "./common.jsx";
import { Scene, headlines } from "./Scenes.jsx";
export function ContextPreview({ font, full = false }) {
  const app = useApp(),
    t = useTranslate();
  const [displayFont, setDisplayFont] = useState(null);
  const pending = displayFont?.id !== font.id;
  useEffect(() => {
    let active = true;
    loadFont(font).then(() => { if (active) setDisplayFont(font); });
    return () => { active = false; };
  }, [font.id]);
  return (
    <div className={full ? "studio-canvas" : "inuse-content"}>
      <div className="studio-context-controls">
        {!full && (
          <button
            className="secondary expandstudio"
            data-studio-open={font.id}
            onClick={() => app.open("studio", font.id)}
          >
            {t("Open preview studio")} ↗
          </button>
        )}
        <div className="context-switch" aria-label="Preview layout">
          <AutoConvertToggle />
          {Object.keys(headlines).map((k) => (
            <button
              key={k}
              data-context={k}
              className={app.context === k ? "active" : ""}
              aria-pressed={app.context === k}
              onClick={() => {
                app.setContext(k);
                app.setHeadline("");
              }}
            >
              {t(k[0].toUpperCase() + k.slice(1))}
            </button>
          ))}
        </div>
        <TypingInput
          id="contexttext"
          className="context-input"
          value={app.headline}
          onChange={app.setHeadline}
          placeholder={t("Try your own headline…")}
          aria-label="Sample headline"
        />
        <p className="context-caption">
          {t("Typography preview")} · {font.name}
        </p>
      </div>
      <div
        className={(full ? "studio-sample-scroll" : "inuse-sample") + (pending ? " scene-loading" : "")}
        aria-busy={pending}
        tabIndex={full ? 0 : undefined}
        aria-label={full ? "Scrollable design sample" : undefined}
      >
        {pending && <div className="scene-loading-cover" role="status"><div className="scene-skeleton" aria-hidden="true"><span/><span/><span/><span/></div><span>{t("Loading font…", "შრიფტი იტვირთება…")}</span></div>}
        <div className="scene-ready-content" style={{visibility:pending ? "hidden" : "visible"}}>
        <Scene
          font={displayFont || font}
          kind={app.context}
          headline={app.headline}
          full={full}
        />
        </div>
      </div>
    </div>
  );
}
export function FontPicker({ font, onSelect }) {
  const { fonts, lang, headline } = useApp(),
    t = useTranslate();
  const [show, setShow] = useState(false),
    [query, setQuery] = useState(""),
    [encoding, setEncoding] = useState(""),
    [type, setType] = useState(""),
    [writing, setWriting] = useState(""),
    [view, setView] = useState("grid"),
    [limit, setLimit] = useState(24);
  const ref = useRef(null);
  const available = fonts.filter((f) => f.file && !f.previewUnsupported);
  const filtered = available.filter(
    (f) =>
      (!encoding || f.encoding.startsWith(encoding)) &&
      (!type || f.classes.includes(type)) &&
      (!writing || f.writings.includes(writing)) &&
      (f.name + " " + f.authors.join(" "))
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  useEffect(() => {
    const handler = (e) => {
      if (!ref.current?.contains(e.target)) setShow(false);
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, []);
  return (
    <div
      className="studio-picker-wrap"
      ref={ref}
      onKeyDown={(e) => {
        if (e.key === "Escape" && show) {
          e.preventDefault();
          e.stopPropagation();
          setShow(false);
        }
      }}
    >
      <button
        id="studiofont"
        className="studio-font-trigger"
        aria-expanded={show}
        aria-controls="studio-picker"
        onClick={() => setShow(!show)}
      >
        <span>
          <b>{font.name}</b>
          <small>
            {font.encoding} · {font.style}
          </small>
        </span>
        <span>⌄</span>
      </button>
      {show && (
        <div id="studio-picker">
          <div className="picker-controls">
            <input
              id="picker-search"
              type="search"
              autoFocus
              value={query}
              placeholder={t("Search fonts…", "მოძებნეთ შრიფტი…")}
              onChange={(e) => {
                setQuery(e.target.value);
                setLimit(24);
              }}
            />
            <select
              id="picker-encoding"
              aria-label="Filter by encoding"
              value={encoding}
              onChange={(e) => {
                setEncoding(e.target.value);
                setLimit(24);
              }}
            >
              <option value="">{t("All encodings", "ყველა კოდირება")}</option>
              {["Unicode", "Latin", "STD8"].map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
            <div className="viewbuttons">
              {["grid", "list"].map((v) => (
                <button
                  key={v}
                  data-picker-view={v}
                  aria-label={`Font picker ${v} view`}
                  className={view === v ? "selected" : ""}
                  onClick={() => setView(v)}
                >
                  <Icon name={v} />
                </button>
              ))}
            </div>
          </div>
          <div className="picker-filters">
            {[
              ["type", "classes", type, setType, "All font styles"],
              [
                "writing",
                "writings",
                writing,
                setWriting,
                "All writing systems",
              ],
            ].map(([id, key, val, set, label]) => (
              <select
                key={id}
                id={"picker-" + id}
                aria-label={label}
                value={val}
                onChange={(e) => {
                  set(e.target.value);
                  setLimit(24);
                }}
              >
                <option value="">{t(label)}</option>
                {[...new Set(available.flatMap((f) => f[key]))]
                  .sort()
                  .map((v) => (
                    <option value={v} key={v}>
                      {t(v)}
                    </option>
                  ))}
              </select>
            ))}
            <button
              data-picker-reset
              onClick={() => {
                setQuery("");
                setType("");
                setWriting("");
                setEncoding("");
                setLimit(24);
              }}
            >
              {t("Reset filters", "გასუფთავება")}
            </button>
          </div>
          <div className="picker-summary">
            <AutoConvertToggle />
            {filtered.length}{" "}
            {t("fonts · Choose a preview", "შრიფტი · აირჩიეთ ნიმუში")}
          </div>
          <div className={"picker-results " + view}>
            {filtered.slice(0, limit).map((f) => (
              <button
                key={f.id}
                className={"picker-font " + (font.id === f.id ? "chosen" : "")}
                data-picker-font={f.id}
                onClick={() => {
                  onSelect(f.id);
                  setShow(false);
                }}
              >
                <span className="picker-fontname">{f.name}</span>
                <FontText
                  as="span"
                  className="picker-specimen"
                  font={f}
                  text={headline || "ქართული ამბავი"}
                />
                <span className="picker-meta">
                  {f.encoding} · {f.style}
                </span>
              </button>
            ))}
            {!filtered.length && (
              <p>{t("No fonts found", "შრიფტი ვერ მოიძებნა")}</p>
            )}
          </div>
          {limit < filtered.length && (
            <button
              className="picker-more"
              onClick={() => setLimit(limit + 24)}
            >
              {t("Show more fonts")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
export function Studio() {
  const app = useApp(),
    t = useTranslate();
  const font = app.fonts.find((f) => f.id === app.modal.id) || app.fonts[0];
  const available = app.fonts.filter((f) => f.file && !f.previewUnsupported);
  function step(n) {
    const index = available.findIndex((f) => f.id === font.id);
    app.open(
      "studio",
      available[(index + n + available.length) % available.length].id,
    );
  }
  return (
    <div className="previewstudio">
      <div className="detailhead">
        <div>
          <p className="eyebrow">FONTLAB · IN CONTEXT</p>
          <h2>{t("Preview studio")}</h2>
        </div>
        <Close onClick={app.close} />
      </div>
      <div className="studio-toolbar">
        <span className="studio-font-label">{t("Choose a font")}</span>
        <FontPicker font={font} onSelect={(id) => app.open("studio", id)} />
        {[-1, 1].map((n) => (
          <button
            key={n}
            className="secondary"
            data-studio-step={n}
            aria-label={n < 0 ? "Previous font" : "Next font"}
            onClick={() => step(n)}
          >
            {n < 0 ? "←" : "→"}
          </button>
        ))}
      </div>
      <ContextPreview font={font} full />
    </div>
  );
}
